import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import LinkIcon from '@mui/icons-material/Link'
import SearchIcon from '@mui/icons-material/Search'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import Markdown from '../../components/shared/Markdown'
import { setAlert } from '../../features/appSlice'
import { prefillBot } from '../../features/assistantSlice'
import { AlertType } from '../../helpers/Constants'
import { filterSections, parseGuide } from '../../helpers/guide'
import AssistantService from '../../services/assistantService'
import {
    APP_BAR_HEIGHT,
    BRAND,
    ICON,
    SURFACE,
    TEXT,
    pageSx,
} from '../../theme/tokens'

/** Bring a heading into view (jsdom has no scrollIntoView). */
const scrollToId = (id) => {
    const el = id ? document.getElementById(id) : null
    if (!el) return false
    if (typeof el.scrollIntoView === 'function')
        el.scrollIntoView({ block: 'start' })
    return true
}

/** The full address of a heading, for its copy-link button. */
export const guideLink = (id) =>
    `${window.location.origin}${process.env.PUBLIC_URL || ''}/guide#${id}`

/** One entry of the contents list: a link to its heading, marked when it is the section in view. */
const ContentsLink = ({ id, title, active, nested, onPick }) => (
    <Box component="li" sx={{ listStyle: 'none' }}>
        <Box
            component={RouterLink}
            to={`#${id}`}
            onClick={() => onPick(id)}
            aria-current={active ? 'location' : undefined}
            sx={{
                display: 'block',
                py: nested ? 0.4 : 0.6,
                pl: nested ? 2.5 : 1.25,
                pr: 1,
                typography: nested ? 'caption' : 'body2',
                fontWeight: active && !nested ? 600 : 400,
                lineHeight: 1.4,
                color: active ? TEXT.primary : TEXT.muted,
                textDecoration: 'none',
                borderLeft: `2px solid ${
                    active ? BRAND.orange : 'transparent'
                }`,
                '&:hover': { color: TEXT.primary },
                '&:focus-visible': {
                    outline: `2px solid ${BRAND.orange}`,
                    outlineOffset: -2,
                },
            }}
        >
            {title}
        </Box>
    </Box>
)

ContentsLink.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    active: PropTypes.bool,
    nested: PropTypes.bool,
    onPick: PropTypes.func.isRequired,
}

/** The contents list: every section shown and its subsections. */
const Contents = ({ sections, activeId, activeSectionId, onPick }) => (
    <Box component="ol" sx={{ m: 0, p: 0 }}>
        {sections.map((section) => (
            <React.Fragment key={section.id}>
                <ContentsLink
                    id={section.id}
                    title={section.title}
                    active={section.id === activeSectionId}
                    onPick={onPick}
                />
                {section.subsections.length > 0 && (
                    <Box component="li" sx={{ listStyle: 'none' }}>
                        <Box component="ol" sx={{ m: 0, p: 0 }}>
                            {section.subsections.map((sub) => (
                                <ContentsLink
                                    key={sub.id}
                                    id={sub.id}
                                    title={sub.title}
                                    nested
                                    active={sub.id === activeId}
                                    onPick={onPick}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </React.Fragment>
        ))}
    </Box>
)

Contents.propTypes = {
    sections: PropTypes.array.isRequired,
    activeId: PropTypes.string,
    activeSectionId: PropTypes.string,
    onPick: PropTypes.func.isRequired,
}

/**
 * One section of the guide. Memoised: its markdown is parsed again only
 * when its text changes, not when the section in view or the search does.
 */
const GuideSection = React.memo(function GuideSection({
    id,
    markdown,
    headingActions,
}) {
    return (
        <Box component="section" aria-labelledby={id} sx={{ mb: 5 }}>
            <Markdown headingActions={headingActions}>{markdown}</Markdown>
        </Box>
    )
})

GuideSection.propTypes = {
    id: PropTypes.string.isRequired,
    markdown: PropTypes.string.isRequired,
    headingActions: PropTypes.func.isRequired,
}

/**
 * The Comms Guide (/guide): how every page works, from service-comms's
 * guide (one source for this page and BhanzuBot's answers). Open to every
 * signed-in user.
 *
 * - A contents list (sticky beside the text; folded above it on a phone)
 *   that marks the section in view.
 * - A search that keeps the sections mentioning the words.
 * - Each section and subsection has a copy-link button (`<origin>/guide#
 *   anchor`), and each section an "Ask BhanzuBot about this" that opens
 *   the panel with "About <section>: " ready to finish.
 * - `/guide#anchor` scrolls to its heading, on load and on every change.
 * - The guide downloaded this session shows at once while it is checked
 *   for a newer one (If-None-Match).
 */
const Guide = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const theme = useTheme()
    const narrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
    const [guide, setGuide] = useState(() => AssistantService.cachedGuide())
    const [loadState, setLoadState] = useState(() =>
        AssistantService.cachedGuide() ? 'ready' : 'loading'
    )
    const [query, setQuery] = useState('')
    const [activeId, setActiveId] = useState('')
    // A link to a heading the search has hidden: shown once the search clears.
    const pendingHash = useRef('')
    const live = useRef(true)
    useEffect(() => {
        live.current = true
        return () => {
            live.current = false
        }
    }, [])

    const load = useCallback(() => {
        if (!AssistantService.cachedGuide()) setLoadState('loading')
        AssistantService.guide()
            .then((copy) => {
                if (!live.current) return
                setGuide(copy)
                setLoadState('ready')
            })
            .catch(() => {
                if (!live.current) return
                // A copy from earlier in the session still reads fine.
                setLoadState(AssistantService.cachedGuide() ? 'ready' : 'error')
            })
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const parsed = useMemo(() => parseGuide(guide?.markdown), [guide])
    const shown = useMemo(
        () => filterSections(parsed.sections, query),
        [parsed, query]
    )
    // The section a subsection belongs to, for marking the contents list.
    const sectionOf = useMemo(() => {
        const map = {}
        parsed.sections.forEach((section) => {
            map[section.id] = section.id
            section.subsections.forEach((sub) => {
                map[sub.id] = section.id
            })
        })
        return map
    }, [parsed])

    const onPick = (id) => {
        setActiveId(id)
        scrollToId(id)
    }

    // `#anchor` in the address: scroll to it once the guide is shown, and
    // on every later change (ScrollToTop leaves hash changes alone).
    useEffect(() => {
        if (loadState !== 'ready') return
        const id = decodeURIComponent(location.hash.replace(/^#/, ''))
        if (!id) return
        if (scrollToId(id)) {
            setActiveId(id)
        } else if (query) {
            pendingHash.current = id
            setQuery('')
        }
    }, [location.hash, loadState])

    useEffect(() => {
        if (!pendingHash.current) return
        if (scrollToId(pendingHash.current)) setActiveId(pendingHash.current)
        pendingHash.current = ''
    }, [shown])

    // Mark the section in view as the page scrolls.
    useEffect(() => {
        if (loadState !== 'ready') return undefined
        if (typeof window.IntersectionObserver !== 'function') return undefined
        const headings = [
            ...document.querySelectorAll(
                '[data-guide-content] h2[id], [data-guide-content] h3[id]'
            ),
        ]
        const visible = new Map()
        const observer = new window.IntersectionObserver(
            (entries) => {
                entries.forEach((entry) =>
                    visible.set(entry.target.id, entry.isIntersecting)
                )
                const first = headings.find((h) => visible.get(h.id))
                if (first) setActiveId(first.id)
            },
            { rootMargin: `-${APP_BAR_HEIGHT}px 0px -65% 0px` }
        )
        headings.forEach((h) => observer.observe(h))
        return () => observer.disconnect()
    }, [loadState, shown])

    // Stable, so the memoised sections below do not parse their markdown
    // again whenever the section in view (or the search) changes.
    const headingActions = useCallback(
        ({ level, id, text }) => {
            if (level !== 2 && level !== 3) return null
            const copyLink = async () => {
                try {
                    await navigator.clipboard.writeText(guideLink(id))
                    dispatch(
                        setAlert({
                            open: true,
                            message: 'Link copied',
                            type: AlertType.success,
                        })
                    )
                } catch {
                    dispatch(
                        setAlert({
                            open: true,
                            message: `The link to ${text} could not be copied.`,
                            type: AlertType.error,
                        })
                    )
                }
            }
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <Tooltip title="Copy link">
                        <IconButton
                            size="small"
                            aria-label={`Copy a link to ${text}`}
                            onClick={copyLink}
                            sx={{ color: TEXT.muted }}
                        >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {level === 2 && (
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<ForumOutlinedIcon fontSize="small" />}
                            onClick={() =>
                                dispatch(prefillBot(`About ${text}: `))
                            }
                            aria-label={`Ask BhanzuBot about ${text}`}
                        >
                            Ask BhanzuBot about this
                        </Button>
                    )}
                </Box>
            )
        },
        [dispatch]
    )

    const contents = (
        <Contents
            sections={shown}
            activeId={activeId}
            activeSectionId={sectionOf[activeId] ?? ''}
            onPick={onPick}
        />
    )
    const needle = query.trim()

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{ color: TEXT.primary, fontWeight: 600 }}
                        >
                            Guide
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.muted, mt: 0.5 }}
                        >
                            How every page of Comms works. BhanzuBot answers
                            from the same text.
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search the guide"
                        inputProps={{ 'aria-label': 'Search the guide' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: TEXT.faint,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', sm: 300 } }}
                    />
                </Box>

                {loadState === 'loading' && (
                    <InlineLoading label="Loading the guide…" py={8} />
                )}
                {loadState === 'error' && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                            py: 4,
                        }}
                    >
                        <ErrorBanner message="The guide could not be loaded. Check your connection and try again." />
                        <Button variant="outlined" onClick={load}>
                            Retry
                        </Button>
                    </Box>
                )}
                {loadState === 'ready' && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                md: '240px minmax(0, 1fr)',
                            },
                            gap: { xs: 2, md: 4 },
                            alignItems: 'start',
                        }}
                    >
                        {narrow ? (
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    background: SURFACE.card,
                                    border: `1px solid ${SURFACE.border}`,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="guide-contents"
                                >
                                    <Typography variant="overline">
                                        Contents
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    id="guide-contents"
                                    component="nav"
                                    aria-label="Contents"
                                >
                                    {contents}
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            <Box
                                component="nav"
                                aria-label="Contents"
                                sx={{
                                    position: 'sticky',
                                    top: APP_BAR_HEIGHT + 16,
                                    maxHeight: `calc(100vh - ${
                                        APP_BAR_HEIGHT + 32
                                    }px)`,
                                    overflowY: 'auto',
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    component="p"
                                    sx={{
                                        color: TEXT.faint,
                                        pl: 1.25,
                                        mb: 0.5,
                                    }}
                                >
                                    Contents
                                </Typography>
                                {contents}
                            </Box>
                        )}

                        <Box data-guide-content sx={{ minWidth: 0 }}>
                            {!needle && parsed.intro && (
                                <Box sx={{ mb: 3 }}>
                                    <Markdown>{parsed.intro}</Markdown>
                                </Box>
                            )}
                            {shown.length === 0 && needle ? (
                                <Typography
                                    role="status"
                                    sx={{
                                        color: TEXT.muted,
                                        typography: 'body1',
                                        py: 6,
                                    }}
                                >
                                    No section mentions &quot;{needle}&quot;.
                                </Typography>
                            ) : (
                                shown.map((section) => (
                                    <GuideSection
                                        key={section.id}
                                        id={section.id}
                                        markdown={section.markdown}
                                        headingActions={headingActions}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default Guide
